import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { computeBeatPulse } from '../beat-pulse.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { getMaxAnisotropy } from '../scene-manager.js';
import { cleanDisplayTitle, cleanDisplayArtist } from '../title-clean.js';

// Shared-rig contract (see the framing addendum). 'forbidden': one quad, one
// fragment shader, no 3D scene for a rig to light.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'typeslam',
  family: 'vector',
  energyLevel: 0.70,
  bpmMin: 110,
  bpmMax: 180,
  movement: 0.80,
  danceability: 0.70,
  tonality: 0.45,
  enabled: true
};

// TypeSlam: a full-frame typographic composition built from the TITLE and the
// ARTIST of whatever is playing.
//
// profile.title and profile.artist were the last pieces of available data the
// graphics engine ignored completely - until now they only ever reached the DOM
// overlay. So every track composes itself differently with no extra source.
//
// Register: Boiler Room idents, brutalist techno poster, Designers Republic,
// Peter Saville, VJ text loops. Heavy condensed type, repetition, scroll, hard
// inversion. Graphic and dry, never decorative.
//
// PERFORMANCE SHAPE: pure fill-rate, like kaleido-drift.js. Two triangles, no
// draw-call pressure, every pixel running the same shader. Budget: 1-2 texture
// reads and ~30 ALU ops per pixel. No loop, no procedural noise, mediump.
// The mode branch is on a UNIFORM, so it is fully coherent - every pixel in a
// frame takes the same path and it costs nothing.
//
// The two CanvasTextures are built ONCE per track and freed on the next build.
// Nothing touches a canvas during playback.

const CAMERA_POS = [0, 2, 8];
const CAMERA_LOOK = [0, 2, 0];

// 1024x256 = 4:1. The shader needs that ratio to keep letters undistorted, so
// it is derived here rather than written twice.
const TEXTURE_W = 1024;
const TEXTURE_H = 256;
const TEXTURE_ASPECT = TEXTURE_W / TEXTURE_H;
// Transparent gutter each side. Without it the repeat wraps the last glyph
// straight into the first and the words weld together.
const SIDE_MARGIN = 8;
const FILL_FRACTION = 0.96; // of the canvas width
const MEASURE_SIZE = 200;
const MAX_SIZE = 190;       // keeps caps inside the 256px height
const LETTER_SPACING = '-0.02em';

// Tried in order; the first that MEASURES non-zero wins. A stack that failed to
// load reports width 0, which is the only reliable signal available here.
const FONT_STACKS = [
  "900 SIZEpx 'Archivo Black', 'Arial Black', 'Impact', 'Helvetica Neue', sans-serif",
  "900 SIZEpx 'Arial Black', 'Impact', sans-serif",
  "900 SIZEpx Impact, sans-serif",
  "bold SIZEpx sans-serif"
];

const LAST_RESORT_LABEL = 'LIVE';
const MAX_LABEL_CHARS = 28;
const TRUNCATE_TO = 24;

const MODE_MARCH = 0;
const MODE_STACK = 1;
const MODE_TUNNEL = 2;
const MODE_SPLIT = 3;
const PHRASE_BEATS = 16;
const PHRASE_SECONDS = 13;

// One full repetition of the title crosses in exactly this many beats.
const BEATS_PER_REPETITION = 4;
const INVERT_FRAMES = 2;
// No bpm: the inversion falls back to wall clock in this range.
const INVERT_SECONDS = [6, 10];

const ROWS_MIN = 3;
const ROWS_MAX = 7;

const BEAT_PULSE_AMOUNT = 0.25;
const BEAT_PULSE_DECAY = 12;

// Deliberately UNDER the bloom threshold (0.78) at rest, so the type stays
// hard-edged and dry rather than glowing. The beat pulse peaks at 1.25, which
// carries it just over - so it flares ON the beat and is crisp between them.
const OFF_WHITE = [0.72, 0.72, 0.69];
const FALLBACK_COLORS = [0xff2fb0, 0x2fd8ff, 0xffb02f];
const SPLIT_REFLECTION = 0.45;

const TIER_LAYOUT = {
  // tier 0 keeps only the two cheapest modes: TUNNEL's 1/y warp and SPLIT's
  // second texture read are the two costly ones.
  0: { modes: [MODE_MARCH, MODE_STACK], maxRows: 4 },
  1: { modes: [MODE_MARCH, MODE_STACK, MODE_TUNNEL, MODE_SPLIT], maxRows: 5 },
  2: { modes: [MODE_MARCH, MODE_STACK, MODE_TUNNEL, MODE_SPLIT], maxRows: 7 }
};
const DEFAULT_TIER = 1;

function progressIntensity(profile) {
  if (profile.duration <= 0) return 0.6;
  return Math.sin(Math.PI * THREE.MathUtils.clamp(profile.progress, 0, 1));
}

// Uppercase, and cut long enough to stay readable. Past ~28 characters the
// repetition turns into a grey ribbon with no word shape left in it.
function prepareLabel(raw) {
  const text = (raw || '').trim().toUpperCase();
  if (text.length === 0) return '';
  if (text.length > MAX_LABEL_CHARS) return text.slice(0, TRUNCATE_TO) + ' ';
  return text + ' ';
}

// Builds one 1024x256 transparent canvas with white type across it. Called
// once per track, never per frame.
function drawTextTexture(label) {
  const canvas = document.createElement('canvas');
  canvas.width = TEXTURE_W;
  canvas.height = TEXTURE_H;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, TEXTURE_W, TEXTURE_H);

  // letterSpacing has to be set BEFORE measureText or the measurement ignores
  // it and the fitted size comes out wrong. Feature-detected: older engines
  // simply do not have it.
  const hasSpacing = 'letterSpacing' in ctx;
  if (hasSpacing) ctx.letterSpacing = LETTER_SPACING;

  let width = 0;
  let stack = null;
  for (let i = 0; i < FONT_STACKS.length; i++) {
    ctx.font = FONT_STACKS[i].replace('SIZE', String(MEASURE_SIZE));
    const w = ctx.measureText(label).width;
    // A stack that failed to load measures 0 - the only signal the canvas API
    // gives for "this font is not actually here".
    if (w > 0) { width = w; stack = FONT_STACKS[i]; break; }
  }
  if (!stack) {
    stack = FONT_STACKS[FONT_STACKS.length - 1];
    ctx.font = stack.replace('SIZE', String(MEASURE_SIZE));
    width = ctx.measureText(label).width || 1;
  }

  const targetWidth = TEXTURE_W * FILL_FRACTION - SIDE_MARGIN * 2;
  const size = Math.min(MAX_SIZE, MEASURE_SIZE * targetWidth / width);
  ctx.font = stack.replace('SIZE', String(size));
  if (hasSpacing) ctx.letterSpacing = LETTER_SPACING;

  const finalWidth = ctx.measureText(label).width;
  ctx.fillStyle = '#ffffff';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillText(label, (TEXTURE_W - finalWidth) / 2, TEXTURE_H / 2);

  const texture = new THREE.CanvasTexture(canvas);
  // RepeatWrapping is the whole point: the shader tiles this horizontally.
  // It is also why the shader must NOT call fract() on the coordinate before
  // sampling - the sampler already wraps, so fract() was redundant, and it
  // made the UV derivative discontinuous at every tile seam, which is what
  // any mip selection is computed from. With mipmaps now on, leaving it in
  // would have drawn a blurred band down each seam.
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.magFilter = THREE.LinearFilter;
  // MIPMAPS. This texture is MINIFIED in normal use, not magnified: at four
  // rows the 256px-tall canvas lands in about a quarter of the frame height,
  // and the quality governor's tier-0 pixel ratio (0.7) takes another 30% off
  // - roughly 0.7x on both axes. Sampled with a plain LinearFilter and no mip
  // chain, that is textbook minification aliasing, and aliasing is only
  // moderately ugly while it holds still: it CRAWLS once the type scrolls,
  // which is exactly the report this came from ("quand elles defilent vite
  // sont super pixelisees").
  //
  // Trilinear rather than LinearMipmapNearest: the type crosses mip levels
  // continuously as it scrolls, and a nearest-mip transition would swap
  // sharpness visibly mid-scroll - trading a crawl for a pop.
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  // The minification is ANISOTROPIC - the rows are squashed vertically far
  // more than horizontally, and TUNNEL's 1/y warp is extreme about it - so
  // isotropic mip selection has to pick the coarser of the two axes and blurs
  // the readable one to protect the other. This is also what keeps the band
  // seams sharp: MARCH/STACK offset u by `band * 0.37`, which is a step
  // function of y, so du/dy is a spike on the one pixel row where floor()
  // steps. Isotropic filtering answers that spike with the coarsest mip and
  // draws a grey line across the frame at every band boundary; anisotropic
  // filtering keeps sampling along the axis that is actually fine.
  texture.anisotropy = getMaxAnisotropy();
  texture.needsUpdate = true;
  return { texture, label, fontStack: stack, fittedSize: size };
}

const vertexShader = `
  precision mediump float;

  varying vec2 vUv;
  varying float vSafeAtt;

  ${SAFE_ZONE_VERTEX_CHUNK}

  void main() {
    vUv = uv;
    // Straight to clip space: the quad ignores the camera entirely, so
    // SceneManager's Ken Burns drift cannot slide a full-frame composition
    // around. w is 1, so the safe zone's own divide is a no-op here.
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
    vSafeAtt = safeZoneAttenuation(gl_Position);
  }
`;

function buildFragmentShader(layout) {
  const hasTunnel = layout.modes.indexOf(MODE_TUNNEL) >= 0;
  const hasSplit = layout.modes.indexOf(MODE_SPLIT) >= 0;

  const tunnel = hasTunnel ? `
    else if (uMode < 2.5) {
      // TUNNEL - rows of type receding to a vanishing point just above centre.
      // The 1/y warp is the whole trick: dividing x by the distance from the
      // horizon is what makes the rows converge.
      vec2 c = vec2((vUv.x - 0.5) * uAspect, vUv.y - 0.56);
      float y = abs(c.y) + 0.045;
      float depth = 0.34 / y;
      float ring = depth - uScroll * 2.0;
      band = floor(ring);
      float u = c.x / y * 0.30 + uScroll * 0.5;
      a = texture2D(uTitle, vec2(u, ring)).a;
      // Fade out at the horizon, or every row piles into one bright line.
      a *= smoothstep(0.0, 0.11, abs(c.y));
    }
  ` : '';

  const split = hasSplit ? `
    else if (uMode < 3.5) {
      // SPLIT - title above, artist below, mirrored about a central horizon.
      float top = step(0.5, vUv.y);
      float vy = abs(vUv.y - 0.5) * 2.0;
      band = top;
      float u = vUv.x * uRepeats * 0.55 + uScroll * (top * 2.0 - 1.0);
      vec2 uv = vec2(u, vy * 1.7);
      a = mix(texture2D(uArtist, uv).a, texture2D(uTitle, uv).a, top);
      // The lower half reads as a reflection, not a second panel.
      dim = mix(${SPLIT_REFLECTION.toFixed(2)}, 1.0, top);
    }
  ` : '';

  return `
  precision mediump float;

  uniform sampler2D uTitle;
  uniform sampler2D uArtist;
  uniform float uMode;
  uniform float uRows;
  uniform float uRepeats;
  uniform float uScroll;
  uniform float uTime;
  uniform float uAspect;
  uniform float uPulse;
  uniform float uInvert;
  uniform vec3 uInvertBg;
  uniform vec3 uAccent0;
  uniform vec3 uAccent1;
  uniform vec3 uAccent2;

  varying vec2 vUv;
  varying float vSafeAtt;

  void main() {
    float a = 0.0;
    float band = 0.0;
    float dim = 1.0;

    if (uMode < 0.5) {
      // MARCH - horizontal bands, alternating direction, own speed each.
      band = floor(vUv.y * uRows);
      float dir = mod(band, 2.0) < 0.5 ? 1.0 : -1.0;
      float speed = 0.75 + 0.5 * fract(band * 0.618);
      float u = vUv.x * uRepeats + dir * uScroll * speed + band * 0.37;
      a = texture2D(uTitle, vec2(u, vUv.y * uRows)).a;
    } else if (uMode < 1.5) {
      // STACK - no scroll at all; each line breathes sideways on its own
      // phase. Still in position, alive in offset.
      band = floor(vUv.y * uRows);
      float u = vUv.x * uRepeats + sin(uTime * 0.22 + band * 1.7) * 0.6 + band * 0.29;
      a = texture2D(uTitle, vec2(u, vUv.y * uRows)).a;
    }${tunnel}${split}

    vec3 offWhite = vec3(${OFF_WHITE[0]}, ${OFF_WHITE[1]}, ${OFF_WHITE[2]});
    // One band in three is accented, and which accent steps along with the
    // band index so the same colour never lands twice in a row.
    float accIdx = mod(floor(band / 3.0), 3.0);
    vec3 accent = accIdx < 0.5 ? uAccent0 : (accIdx < 1.5 ? uAccent1 : uAccent2);
    vec3 ink = mod(band, 3.0) < 0.5 ? accent : offWhite;

    // Hard inversion: ground floods to the dominant colour, type goes black.
    vec3 ground = uInvertBg * uInvert;
    vec3 type = mix(ink, vec3(0.0), uInvert);

    // The safe zone ATTENUATES rather than cuts, so the type carries on
    // visibly behind the cover instead of leaving a hole in the composition.
    vec3 col = mix(ground, type, a) * dim * uPulse * vSafeAtt;
    gl_FragColor = vec4(col, 1.0);
  }
  `;
}

export class TypeSlamVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER_LAYOUT[tier] ? tier : DEFAULT_TIER;
    // Read by the acceptance check for "no canvas work during playback": it
    // must not move once a track is up.
    this.textureBuilds = 0;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.position.set(CAMERA_POS[0], CAMERA_POS[1], CAMERA_POS[2]);
    camera.lookAt(CAMERA_LOOK[0], CAMERA_LOOK[1], CAMERA_LOOK[2]);

    const layout = TIER_LAYOUT[this._tier];
    this._modes = layout.modes;
    this._maxRows = layout.maxRows;

    this._uniforms = Object.assign({}, safeZoneUniforms, {
      uTitle: { value: null },
      uArtist: { value: null },
      uMode: { value: this._modes[0] },
      uRows: { value: ROWS_MIN },
      uRepeats: { value: 2 },
      uScroll: { value: 0 },
      uTime: { value: 0 },
      uAspect: { value: 16 / 9 },
      uPulse: { value: 1 },
      uInvert: { value: 0 },
      uInvertBg: { value: new THREE.Color(FALLBACK_COLORS[0]) },
      uAccent0: { value: new THREE.Color(FALLBACK_COLORS[0]) },
      uAccent1: { value: new THREE.Color(FALLBACK_COLORS[1]) },
      uAccent2: { value: new THREE.Color(FALLBACK_COLORS[2]) }
    });

    const geometry = new THREE.PlaneGeometry(1, 1);
    this._material = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: vertexShader,
      fragmentShader: buildFragmentShader(layout),
      transparent: false,
      depthTest: false,
      depthWrite: false
    });
    this._quad = new THREE.Mesh(geometry, this._material);
    this._quadGeometry = geometry;
    this._quad.frustumCulled = false;
    this._quad.renderOrder = -1000;
    scene.add(this._quad);

    this._time = 0;
    this._scroll = 0;
    this._modeIndex = 0;
    this._lastBeatIndex = null;
    this._phraseEndsAtBeat = PHRASE_BEATS;
    this._phraseEndsAtTime = PHRASE_SECONDS;
    this._invertFrames = 0;
    this._nextInvertAtTime = INVERT_SECONDS[0];
    // CHANTIER 6 (revision) - edge-detection for the accentPhrase-driven
    // inversion trigger (see update()).
    this._lastAccentPhrase = null;
    // Phase 5 validation - edge-detection for director.bandPeak.low's own
    // rising edge, the analysis-driven equivalent above.
    this._lastBandPeakLow = null;
    this._lastLabelKey = null;
    this._lastPaletteRef = null;
    this._titleInfo = null;
    this._artistInfo = null;

    // Something must be on screen before the first poll resolves.
    this._buildTextures({ title: '', artist: '', album: '' });
  }

  // The cascade the brief demands: a radio stream routinely has no title at
  // all, and an empty frame is never acceptable.
  //
  // profile.title is cleaned before it enters the cascade - Sonos's raw
  // title carries "(feat. X)", "- Radio Edit", "(Extended Mix)" noise that
  // has no business filling a wall of repeated type (see title-clean.js).
  // artist/album are left as-is: they rarely carry this kind of suffix, and
  // this is scoped to the one field the noise actually comes from.
  _resolveLabels(profile) {
    const candidates = [cleanDisplayTitle(profile.title), cleanDisplayArtist(profile.artist), profile.album];
    let title = '';
    for (let i = 0; i < candidates.length && !title; i++) title = prepareLabel(candidates[i]);
    if (!title) title = prepareLabel(LAST_RESORT_LABEL);

    const artistCandidates = [cleanDisplayArtist(profile.artist), profile.album, cleanDisplayTitle(profile.title)];
    let artist = '';
    for (let i = 0; i < artistCandidates.length && !artist; i++) artist = prepareLabel(artistCandidates[i]);
    if (!artist) artist = title;
    return { title, artist };
  }

  _buildTextures(profile) {
    const labels = this._resolveLabels(profile);
    const previousTitle = this._titleInfo;
    const previousArtist = this._artistInfo;

    this._titleInfo = drawTextTexture(labels.title);
    this._artistInfo = drawTextTexture(labels.artist);
    this._uniforms.uTitle.value = this._titleInfo.texture;
    this._uniforms.uArtist.value = this._artistInfo.texture;
    this.textureBuilds++;

    // Freed on the NEXT build, not left to the GC - a CanvasTexture holds a
    // GPU allocation that renderer.info.memory counts.
    if (previousTitle) previousTitle.texture.dispose();
    if (previousArtist) previousArtist.texture.dispose();
  }

  _switchMode() {
    this._modeIndex = (this._modeIndex + 1) % this._modes.length;
    this._uniforms.uMode.value = this._modes[this._modeIndex];
  }

  _applyPalette(profile) {
    const palette = profile.artworkPalette;
    const vivid = (palette && palette.vividColors) || [];
    const dominant = (palette && palette.dominantColors) || [];
    const slots = [this._uniforms.uAccent0.value, this._uniforms.uAccent1.value, this._uniforms.uAccent2.value];
    for (let i = 0; i < 3; i++) {
      slots[i].set(vivid.length > 0 ? vivid[i % vivid.length] : FALLBACK_COLORS[i]);
    }
    this._uniforms.uInvertBg.value.set(dominant.length > 0 ? dominant[0] : FALLBACK_COLORS[0]);
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    if (!paused) this._time += deltaTime;
    this._uniforms.uTime.value = this._time;

    // Rebuilt only when the words themselves change - this is the single
    // guard that keeps every canvas operation out of the playing frame.
    const key = (profile.title || '') + ' ' + (profile.artist || '') + ' ' + (profile.album || '');
    if (key !== this._lastLabelKey) {
      this._lastLabelKey = key;
      this._buildTextures(profile);
    }
    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._applyPalette(profile);
    }

    const rows = Math.round(THREE.MathUtils.clamp(
      ROWS_MIN + (ROWS_MAX - ROWS_MIN) * progressIntensity(profile), ROWS_MIN, this._maxRows));
    this._uniforms.uRows.value = rows;
    this._uniforms.uAspect.value = this._camera.aspect;
    // Repetitions across the frame that keep the 4:1 texture undistorted at
    // this band height - derived, so letters never stretch when rows change.
    this._uniforms.uRepeats.value = Math.max(1, this._camera.aspect * rows / TEXTURE_ASPECT);

    // Scroll comes straight off the ABSOLUTE beat clock rather than being
    // integrated frame by frame: one full repetition per BEATS_PER_REPETITION
    // beats, exactly, with no accumulated drift.
    const hasBeat = !paused && profile.beatIndex != null && profile.beatPhase != null;
    if (hasBeat) {
      this._scroll = (profile.beatIndex + profile.beatPhase) / BEATS_PER_REPETITION;
    } else if (!paused) {
      // No tempo: fall back to wall clock at the reference pace.
      this._scroll += deltaTime * (120 / 60) / BEATS_PER_REPETITION;
    }
    this._uniforms.uScroll.value = this._scroll;

    // Phrase mode-switching keys off beatIndex, a whole-beat count since
    // track start - so "phrase boundary crossed" is exact rather than
    // inferred from a rising edge that could be missed on a slow frame.
    //
    // CHANTIER 6 (revision): the inversion flash used to fire on
    // director.accentPhrase's own rising edge (last 2 beats of every
    // 16-beat phrase, see director.js) instead of every single bar
    // downbeat - once per phrase reads as a deliberate accent; once every
    // 4 beats read as a near-constant strobe.
    // Phase 5 validation: with a real analysis timeline, it fires on
    // director.bandPeak.low's own rising edge instead (the actual kick),
    // same exact-equality pattern as kinetic-rig.js/liquid-lens.js. Falls
    // back to the accentPhrase behavior unchanged whenever there's no
    // analysis.
    const hasAnalysisBands = !!profile.analysisTimeline;
    if (!paused) {
      if (hasAnalysisBands) {
        const bandPeakLow = profile.director.bandPeak.low;
        if (hasBeat && bandPeakLow === 1 && this._lastBandPeakLow !== 1) {
          this._invertFrames = INVERT_FRAMES;
        }
        this._lastBandPeakLow = bandPeakLow;
        this._lastAccentPhrase = null;
      } else {
        const accentPhrase = profile.director ? profile.director.accentPhrase : null;
        const accentRisingEdge = accentPhrase === 1 && this._lastAccentPhrase !== 1;
        this._lastAccentPhrase = accentPhrase;
        if (hasBeat && accentRisingEdge) {
          this._invertFrames = INVERT_FRAMES;
        } else if (!hasBeat && this._time >= this._nextInvertAtTime) {
          this._invertFrames = INVERT_FRAMES;
          this._nextInvertAtTime = this._time
            + INVERT_SECONDS[0] + Math.random() * (INVERT_SECONDS[1] - INVERT_SECONDS[0]);
        }
        this._lastBandPeakLow = null;
      }
    }

    if (hasBeat) {
      if (this._lastBeatIndex !== profile.beatIndex) {
        if (profile.beatIndex >= this._phraseEndsAtBeat) {
          this._switchMode();
          this._phraseEndsAtBeat = profile.beatIndex + PHRASE_BEATS;
        }
        this._lastBeatIndex = profile.beatIndex;
      }
    } else {
      this._lastBeatIndex = null;
      if (!paused && this._time >= this._phraseEndsAtTime) {
        this._switchMode();
        this._phraseEndsAtTime = this._time + PHRASE_SECONDS;
      }
    }

    // Exactly INVERT_FRAMES frames, counted in frames rather than seconds:
    // the brief asks for a hard two-frame stamp, not a short fade.
    // PUBLISH first, then decrement. Decrementing first burned one of the two
    // frames before the uniform was ever written, so the flash lasted one.
    this._uniforms.uInvert.value = this._invertFrames > 0 ? 1 : 0;
    if (this._invertFrames > 0 && !paused) this._invertFrames--;

    // Phase 5 validation - director.bands.high (real analysis) modulates
    // the pulse continuously instead of the plain beatPhase LFO.
    this._uniforms.uPulse.value = hasAnalysisBands
      ? profile.director.bands.high
      : computeBeatPulse(profile.beatPhase, BEAT_PULSE_AMOUNT, BEAT_PULSE_DECAY);
  }

  dispose() {
    this._scene.remove(this._quad);
    this._quadGeometry.dispose();
    this._material.dispose();
    if (this._titleInfo) { this._titleInfo.texture.dispose(); this._titleInfo = null; }
    if (this._artistInfo) { this._artistInfo.texture.dispose(); this._artistInfo = null; }
  }
}
